import { FolderKanban, ListChecks, LogOut, Settings, Users, Download, Cpu, ShieldCheck } from "lucide-react";
import { NavLink, useParams } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { user, logout } = useAuth();
  const { projectId } = useParams<{ projectId: string }>();
  const isAdmin = !!user?.is_superuser;

  const topItems: NavItem[] = [
    { to: "/projects", label: "Projects", icon: <FolderKanban size={18} />, end: false },
    ...(isAdmin
      ? [{ to: "/users", label: "Users", icon: <ShieldCheck size={18} /> } as NavItem]
      : []),
  ];

  // Settings is admin-only; everything else is for project members.
  const projectItems: NavItem[] = projectId
    ? [
        { to: `/projects/${projectId}/tasks`, label: "Tasks", icon: <ListChecks size={18} /> },
        ...(isAdmin
          ? [
              { to: `/projects/${projectId}/members`, label: "Members", icon: <Users size={18} /> } as NavItem,
              { to: `/projects/${projectId}/settings`, label: "Settings", icon: <Settings size={18} /> } as NavItem,
              { to: `/projects/${projectId}/export`, label: "Export", icon: <Download size={18} /> } as NavItem,
            ]
          : []),
      ]
    : [];

  return (
    <aside className="flex h-screen w-60 shrink-0 flex-col border-r border-border bg-bg-surface">
      <div className="flex items-center gap-3 px-4 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-accent-hover">
          <Cpu size={20} className="text-bg" />
        </div>
        <div>
          <div className="text-sm font-semibold leading-tight">DroneTag</div>
          <div className="text-[11px] uppercase tracking-wider text-text-dim">Audio Labeling</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-2">
        <SectionLabel>Navigation</SectionLabel>
        <NavList items={topItems} onNavigate={onNavigate} />

        {projectItems.length > 0 && (
          <>
            <SectionLabel className="mt-6">Project</SectionLabel>
            <NavList items={projectItems} onNavigate={onNavigate} />
          </>
        )}
      </nav>

      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-elevated text-xs font-medium text-text-muted">
            {(user?.display_name || user?.username || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.display_name || user?.username}</div>
            <div className="truncate text-xs text-text-dim">@{user?.username}</div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="rounded p-1.5 text-text-dim hover:bg-bg-elevated hover:text-text"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-text-dim", className)}>
      {children}
    </div>
  );
}

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-bg-elevated text-text"
                  : "text-text-muted hover:bg-bg-elevated hover:text-text",
              )
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
